import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormField,
  FormSelect,
} from "@/components/ui";
import { getErrorMessage } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { dollarsToCents } from "@/lib/money";
import { useCreateExpense } from "@/api/hooks/useExpenses";
import {
  EXPENSE_CATEGORY_LABELS,
  ExpenseCategorySchema,
  type ExpenseCategory,
} from "@/api/types/expense";
import { toIsoDate } from "./financeDate";

const CATEGORY_OPTIONS = ExpenseCategorySchema.options;

interface AddExpenseDialogProps {
  open: boolean;
  onClose: () => void;
}

const EMPTY_FORM = {
  date: toIsoDate(new Date()),
  amount: "",
  category: "supplies" as ExpenseCategory,
  vendor: "",
  description: "",
};

/**
 * Manual expense entry — a plain controlled form (no react-hook-form) to
 * match EventMoneySection's EventCostsCard convention for money-input
 * dialogs: dollar strings in local state, converted to integer cents via
 * dollarsToCents(...) only at submit time (the API stores the price, this
 * form never computes one).
 */
export function AddExpenseDialog({ open, onClose }: AddExpenseDialogProps) {
  const createExpense = useCreateExpense();
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (open) setForm(EMPTY_FORM);
  }, [open]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await createExpense.mutateAsync({
        date: form.date,
        amount_cents: dollarsToCents(form.amount),
        category: form.category,
        vendor: form.vendor || undefined,
        description: form.description || undefined,
      });
      toastSuccess("Expense added");
      onClose();
    } catch (err) {
      toastError("Couldn't add expense", getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Add expense">
      <DialogContent size="md">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader onClose={onClose}>Add expense</DialogHeader>
          <DialogBody className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                label="Date"
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
              />
              <FormField
                label="Amount"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                required
                value={form.amount}
                onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
              />
            </div>

            <FormSelect
              label="Category"
              value={form.category}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, category: e.target.value as ExpenseCategory }))
              }
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {EXPENSE_CATEGORY_LABELS[c]}
                </option>
              ))}
            </FormSelect>

            <FormField
              label="Vendor"
              value={form.vendor}
              onChange={(e) => setForm((prev) => ({ ...prev, vendor: e.target.value }))}
            />

            <FormField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={createExpense.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createExpense.isPending}>
              {createExpense.isPending ? "Saving..." : "Add expense"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
